import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Route, useHistory, useLocation } from 'react-router-dom'
import { darkTheme, DialogContainer, lightTheme, Provider as V3Provider, ProgressCircle, AlertDialog, Text } from '@adobe/react-spectrum'
import { Provider as ReduxProvider, useSelector, useDispatch } from 'react-redux'
import Provider from '@react/react-spectrum/Provider'
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js'
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react'
import Chat from './Apps/Chat/Chat'
import Phone from './Apps/Phone'
import AllConversations from './Apps/Chat/Modules/AllConversations/AllConversations'
import { NavigationMenu } from './Layout'
import NetworkNotification from './Layout/Notification/NetworkNotification'
import store from './redux/store'
import './Theme/Styles/styles.scss'
import Login from './Layout/Login/Login'
import oktaconfig from './okta-stage-config'
import Profile from './Profile'
import Logout from './Layout/Login/Logout'
import Notification from './Layout/Notification/Notification'
import Offline from './Apps/ContactCenter/Layout/Offline/Offline'
import ContactCenter from './Apps/ContactCenter/Modules/ContactCenter'
import useOnLoad from './hooks/useOnLoad'
import { IncomingCall } from './Apps/ContactCenter/Layout/IncomingCall'
import { MissedCall } from './Apps/ContactCenter/Layout/MissedCall'
import { FailurePopup } from './Apps/ContactCenter/Layout/FailurePopup'
import RemoveParticipant from './Layout/Notification/RemoveParticipant'
import { updateLastAgentStatusOnVpn } from './redux/actions/preferencesActions'
import { setAuthFail } from './redux/actions/contactCenterActions'
import { AUTH_ERROR_MESSAGES } from './constants/authErrorConstants'
import localization from './Apps/Chat/Components/lang/localization'
import { RootState } from './redux/reducers/rootReducer'
import { checkMockOfferIdEligibility } from './redux/actions/CheckEligibilityActions';
import { vpnConnectionStateHandler } from './helpers/vpn-connection-state-handler'
import VpnRequired from './page-components/vpn-validation/VpnRequired/vpn-required-component'
import VpnValidation from './page-components/vpn-validation/VpnValidation/vpn-validation-component'
import { checkAllPermissions } from './utils/permission-utils';
import { LOADING_STAGES } from './redux/constants/preferencesConstants'

const AGENT_STATE_VALUES = ['Pending', 'CallingCustomer', 'Missed', 'Busy', 'AfterCallWork', 'Offline', 'PendingBusy'];

// Helper function to get loading message based on stage
const getLoadingMessage = (stage: string): string => {
  switch (stage) {
    case LOADING_STAGES.AUTHENTICATING:
      return 'Authenticating...'
    case LOADING_STAGES.SAML_LOGIN:
      return 'Attempting SAML login...'
    case LOADING_STAGES.BACKEND_AUTH:
      return 'Authenticating with backend...'
    case LOADING_STAGES.CCP_INITIALIZING:
      return 'Initializing CCP, please wait...'
    case LOADING_STAGES.COMPLETE:
      return 'Loading complete'
    default:
      return 'Loading...'
  }
}

const ThemeContainer = ({ hasRequiredPermissions, oktaAuth }: { hasRequiredPermissions: boolean, oktaAuth: any }) => {
  const location = useLocation()
  
  // Routes where useOnLoad should NOT be triggered
  const excludedRoutes = ['/logout', '/403', '/', '/login/callback']
  const shouldInitialize = !excludedRoutes.includes(location.pathname)
  
  // Call useOnLoad for all routes except excluded ones
  useOnLoad(oktaAuth, shouldInitialize, hasRequiredPermissions)
  const showCCPLoader = ['/chat', '/history']
  const [networkMode, setNetworkMode] = useState('')
  const { darkMode: globalDarkMode, language, pageHistoryPathname, isVpnConnected, lastAgentStatusWhenVpnIsOn, releaseNotes: releaseNoteState, showLoaderOnOAC, loadingStage } = useSelector((state: RootState) => state.preferences)
  const { currentConversation, notification, alertData, CCPStatus, incomingCallData, missedCallData, recordingDisabled, participantLeft, agentDetails, isAuthFail, authFailType } = useSelector((state: RootState) => state.contactCenter)
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false);
  const [releaseNoteOpen, releaseNotePopUp] = useState(false)
  const V3Theme = globalDarkMode ? darkTheme : lightTheme
  const V2Theme = globalDarkMode ? 'dark' : 'light'
  const agentDesktopClass = globalDarkMode ? 'agent-desktop agent-desktop-scroll-dark' : 'agent-desktop agent-desktop-scroll-light'
  const { ldap = '' } = agentDetails;

  React.useEffect(() => {
    const body: any = document.getElementsByTagName('BODY')[0]
    body.className = globalDarkMode ? 'agent-desktop-scroll-dark' : 'agent-desktop-scroll-light'
    return () => {
      body.className = ''
    }
  }, [agentDesktopClass, globalDarkMode])

  React.useEffect(() => {
    vpnConnectionStateHandler.initializeVpnCheckContext(setNetworkMode)
    return () => {
      vpnConnectionStateHandler.clearVpnCheckContext();
    }
  }, [pageHistoryPathname])

  React.useEffect(() => {
    const fetchFeatureFlag = async () => {
      try {
        dispatch(checkMockOfferIdEligibility());
      }
      catch (error) {
        console.log(error);
      }
    }
    if (ldap)
      fetchFeatureFlag()
  }, [ldap])

  React.useEffect(() => {
    if (isVpnConnected && lastAgentStatusWhenVpnIsOn && CCPStatus.ccpState === "VPN Disconnect") {
      console.log(`VPN_DISCONNECT_CHECK - VPN reconnected and setting status set to ${lastAgentStatusWhenVpnIsOn} from ${CCPStatus.ccpState}`)
      ContactCenter.setStatus(lastAgentStatusWhenVpnIsOn)
    }
    if (!isVpnConnected) {
      console.log(`VPN_DISCONNECT_CHECK -( VPN Event ) As there is no Active contact, setting status to VPN Disconnect due to VPN Disconnect`)
      ContactCenter.setStatus("VPN Disconnect")
    }
    setIsOpen(!isVpnConnected);
  }, [isVpnConnected])

  React.useEffect(() => {
    console.log(`VPN_DISCONNECT_CHECK -State Value Received as ${CCPStatus.ccpState} & VPN Status is ${isVpnConnected}`)
    if (isVpnConnected === true && !AGENT_STATE_VALUES.includes(CCPStatus.ccpState)) {
      console.log(`VPN_DISCONNECT_CHECK - redux status value set to ${CCPStatus.ccpState}`)
      dispatch(updateLastAgentStatusOnVpn(CCPStatus.ccpState))
    }
  }, [CCPStatus.ccpState])

  const dialogDismiss = (action: any, data: any) => {
    return dispatch({ type: action, payload: data })
  }

  const handleAuthFail = () => {
    dispatch(setAuthFail(false, null))
  }

  return (
    <V3Provider theme={V3Theme} colorScheme='light'>
      <Provider theme={V2Theme}>
        {!CCPStatus.isInit && hasRequiredPermissions && showCCPLoader.indexOf(location.pathname) >= 0 && pageHistoryPathname !== '/403' && (
          <div className='ccp-loader'>
            <ProgressCircle aria-label='Loading…' isIndeterminate />
            <div style={{ marginTop: '16px', fontSize: '14px', color: globalDarkMode ? '#fff' : '#000' }}>
              {getLoadingMessage(loadingStage)}
            </div>
          </div>
        )}
        {showLoaderOnOAC && hasRequiredPermissions && pageHistoryPathname !== '/403' && (
          <div className='ccp-loader'>
            <ProgressCircle aria-label='Loading…' isIndeterminate />
            <div style={{ marginTop: '16px', fontSize: '14px', color: globalDarkMode ? '#fff' : '#000' }}>
              {getLoadingMessage(loadingStage)}
            </div>
          </div>
        )}
        <Router>
          <VpnValidation>
            <div className={agentDesktopClass}>
              <div id='ccpContainer' style={{ display: 'none' }} />
              <SecureRoute path='/(!?chat|call|history)' component={NavigationMenu} />
              <main>
                <Route path='/login/callback' exact component={LoginCallback} />
                <SecureRoute path='/chat' component={Chat} />
                <SecureRoute path='/call' component={Phone} />
                <SecureRoute path='/profile' component={Profile} />
                <SecureRoute path='/history' component={AllConversations} />
                <Route path='/logout' component={Logout} />
                <Route path='/403' component={VpnRequired} />
                <Route path='/' exact component={Login} />
              </main>
            </div>
          </VpnValidation>
          {notification.show && <Notification notification={notification} currentConversation={currentConversation} language={language} />}
          <DialogContainer onDismiss={() => dialogDismiss("SETOFFLINEINDICATOR", { isOpen: false })} type='modal'>
            {alertData?.isOpen && <Offline language={language} alertData={alertData} />}
          </DialogContainer>
          <DialogContainer onDismiss={() => dialogDismiss("INCOMING_CALL", { openPopup: "no" })} type='modal'>
            {incomingCallData?.openPopup === "yes" && <IncomingCall darkMode={globalDarkMode} incomingCallData={incomingCallData} />}
          </DialogContainer>
          {
            participantLeft && <RemoveParticipant lang={language} />
          }
          <DialogContainer onDismiss={() => dialogDismiss("MISSED_CALL", { isMissed: false })} type='modal'>
            {missedCallData?.isMissed && <MissedCall darkMode={globalDarkMode} missedCallData={missedCallData} />}
          </DialogContainer>
          <DialogContainer onDismiss={() => dialogDismiss("RECORDING_DISABLED", { ...recordingDisabled, failed: false })} type='modal'>
            {recordingDisabled.failed && <FailurePopup language={language} darkMode={globalDarkMode} />}
          </DialogContainer>
          {<DialogContainer onDismiss={() => setIsOpen(false)} type='modal'>
            {isOpen && <AlertDialog
              title="VPN_DISCONNECTED OR FAILED_TO_VERIFY"
              variant="error"
              primaryActionLabel="Close">
              {localization[language].NOTIFICATIONS.VPN_DISCONNECTED_MESSAGE}
            </AlertDialog>}
          </DialogContainer>
          }
          {<DialogContainer onDismiss={() => releaseNotePopUp(false)} type='modal'>
            {releaseNoteOpen && <AlertDialog
              title={localization[language].NOTIFICATIONS.RELEASE_NOTES + ' v' + releaseNoteState.version}
              primaryActionLabel="Close">
              {localization[language].NOTIFICATIONS.WHAT_NEW}
              {releaseNoteState[`releaseNotes_${language}`].length ?
                releaseNoteState[`releaseNotes_${language}`].map((notes) => {
                  return <li>{notes}</li>
                }) : ""}
            </AlertDialog>}
          </DialogContainer>
          }
          <DialogContainer onDismiss={() => handleAuthFail()} type='modal'>
            {isAuthFail && authFailType && (() => {
              const { title, body } = AUTH_ERROR_MESSAGES[authFailType]

              const renderAuthErrorBody = (bodyText: string) => {
                const lines = bodyText.split('\n').map(line => line.trim()).filter(line => line.length > 0)
                const firstLine = lines[0] || ''
                const bulletPoints = lines.slice(1).filter(line => line.startsWith('•'))
                
                return (
                  <>
                    {firstLine && <Text>{firstLine}</Text>}
                    {bulletPoints.length > 0 && (
                      <div style={{ marginTop: '10px', paddingLeft: '20px' }}>
                        {bulletPoints.map((point, index) => (
                          <Text key={index} UNSAFE_style={{ display: 'block', marginBottom: '5px' }}>
                            • {point.replace('• ', '')}
                          </Text>
                        ))}
                      </div>
                    )}
                  </>
                )
              }
              
              return <AlertDialog
                title={title}
                variant="error"
                primaryActionLabel="OK"
                onPrimaryAction={() => handleAuthFail()}  
              >
                {renderAuthErrorBody(body)}
              </AlertDialog>
            })()}
          </DialogContainer>
          {!notification.show && networkMode === 'Offline' && <NetworkNotification language={language} networkMode={networkMode} setNetworkMode={() => setNetworkMode('')} />}
        </Router>
      </Provider>
    </V3Provider>
  )
}

const oktaAuth = new OktaAuth(oktaconfig.oidc)

const App = () => {
  const history = useHistory()
  const [hasRequiredPermissions, setHasRequiredPermissions] = useState<boolean>(false)

  const restoreOriginalUri = async (_oktaAuth: any, originalUri: string) => {
    history.replace(toRelativeUrl(originalUri, window.location.origin))
    window.location.reload()
  }

  // Check permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const result = await checkAllPermissions()
        setHasRequiredPermissions(result.allPermissionsGranted)
      } catch (error) {
        console.error('Error checking permissions in App:', error)
        setHasRequiredPermissions(false)
      }
    }
    
    checkPermissions()
  }, [])
  
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <ReduxProvider store={store}>
        <ThemeContainer hasRequiredPermissions={hasRequiredPermissions} oktaAuth={oktaAuth} />
      </ReduxProvider>
    </Security>
  )
}

export default App